// ============================================
// FILE: frontend/src/components/matching/MatchCard.jsx
// PURPOSE: Display individual match card with scores and actions
// ============================================

import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MatchScoreIndicator } from './MatchScoreIndicator';
import { Eye, CheckCircle, XCircle, Loader2, Link2 } from 'lucide-react';
import { format } from 'date-fns';

// ========================================
// MAIN COMPONENT
// ========================================

export function MatchCard({
  match,
  onViewDetails,
  onApprove,
  onReject,
  onConnect,
  isReviewing = false,
  isConnecting = false,
  isNew = false
}) {
  console.log('[MatchCard] Rendering match card:', {
    matchId: match.id,
    score: match.matchScore,
    status: match.status,
    isNew
  });

  // ========================================
  // HELPER FUNCTIONS
  // ========================================

  const getStatusColor = (status) => {
    console.log('[MatchCard] Getting status color for:', status);
    switch (status) {
      case 'pending_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-blue-100 text-blue-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'connected':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date) => {
    console.log('[MatchCard] Formatting date:', date);
    try {
      return format(new Date(date), 'PPP');
    } catch (err) {
      console.error('[MatchCard] ❌ Date formatting error:', err);
      return 'Invalid date';
    }
  };

  // ========================================
  // EVENT HANDLERS
  // ========================================

  const handleApprove = () => {
    console.log('[MatchCard] Approve clicked for:', match.id);
    if (onApprove) {
      onApprove(match.id);
    }
  };

  const handleReject = () => {
    console.log('[MatchCard] Reject clicked for:', match.id);
    if (onReject) {
      onReject(match.id);
    }
  };

  const handleViewDetails = () => {
    console.log('[MatchCard] View details clicked for:', match.id);
    if (onViewDetails) {
      onViewDetails(match.id);
    }
  };

  const handleConnect = () => {
    console.log('[MatchCard] Connect clicked for:', match.id);
    if (onConnect) {
      onConnect(match.id);
    }
  };

  // ========================================
  // RENDER
  // ========================================

  console.log('[MatchCard] Rendering with status:', match.status);

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            {/* ========================================
                MATCH SCORE
                ======================================== */}
            <div className="mb-3">
              <MatchScoreIndicator score={match.matchScore} />
            </div>

            {/* ========================================
                STATUS AND BADGES
                ======================================== */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={getStatusColor(match.status)}>
                {match.status === 'pending_review' ? 'Pending Review' : match.status}
              </Badge>
              
              {isNew && (
                <Badge variant="default" className="bg-gradient-to-r from-blue-600 to-purple-600">
                  NEW
                </Badge>
              )}
            </div>
          </div>

          {/* ========================================
              MATCH DATE
              ======================================== */}
          <div className="text-right text-xs text-muted-foreground">
            <p>{formatDate(match.createdAt)}</p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pb-4">
        {/* ========================================
            SCORE BREAKDOWN
            ======================================== */}
        <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-muted rounded-lg">
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Location</p>
            <p className="text-sm font-semibold">{match.locationScore}/30</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Capital</p>
            <p className="text-sm font-semibold">{match.capitalScore}/30</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Project</p>
            <p className="text-sm font-semibold">{match.projectTypeScore}/20</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">Timeline</p>
            <p className="text-sm font-semibold">{match.timelineScore}/10</p>
          </div>
          <div className="text-center col-span-2">
            <p className="text-xs text-muted-foreground">Land Size</p>
            <p className="text-sm font-semibold">{match.landSizeScore}/10</p>
          </div>
        </div>

        {/* ========================================
            ADMIN NOTES
            ======================================== */}
        {match.adminNotes && (
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-4">
            <p className="text-xs font-semibold text-blue-900 mb-1">Admin Notes:</p>
            <p className="text-xs text-blue-800">{match.adminNotes}</p>
          </div>
        )}

        {/* ========================================
            REJECTION REASON
            ======================================== */}
        {match.rejectionReason && match.status === 'rejected' && (
          <div className="p-3 bg-red-50 rounded-lg border border-red-200">
            <p className="text-xs font-semibold text-red-900 mb-1">Rejection Reason:</p>
            <p className="text-xs text-red-800">{match.rejectionReason}</p>
          </div>
        )}
      </CardContent>

      <CardFooter className="gap-2 flex-wrap bg-muted/50 pt-4">
        {/* ========================================
            ACTION BUTTONS
            ======================================== */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleViewDetails}
          disabled={isReviewing || isConnecting}
        >
          <Eye className="w-4 h-4 mr-2" />
          View Details
        </Button>

        {/* ========================================
            PENDING REVIEW ACTIONS
            ======================================== */}
        {match.status === 'pending_review' && (
          <>
            <Button
              variant="default"
              size="sm"
              className="bg-green-600 hover:bg-green-700"
              onClick={handleApprove}
              disabled={isReviewing || isConnecting}
            >
              {isReviewing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </>
              )}
            </Button>

            <Button
              variant="destructive"
              size="sm"
              onClick={handleReject}
              disabled={isReviewing || isConnecting}
            >
              {isReviewing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </>
              )}
            </Button>
          </>
        )}

        {/* ========================================
            APPROVED ACTIONS - CONNECT
            ======================================== */}
        {match.status === 'approved' && (
          <Button
            variant="default"
            size="sm"
            className="gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            onClick={handleConnect}
            disabled={isConnecting || isReviewing}
          >
            {isConnecting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4" />
                Connect Parties
              </>
            )}
          </Button>
        )}

        {/* ========================================
            CONNECTED STATUS - DISABLED
            ======================================== */}
        {match.status === 'connected' && (
          <Badge className="bg-green-100 text-green-800 text-xs px-3 py-1">
            Already Connected
          </Badge>
        )}
      </CardFooter>
    </Card>
  );
}